import { IconButton, InputBaseComponentProps, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Answered, ColumnInfo, LabeledScore, QuestionnaireRisk, RiskInfo, Scores, ScoreUpdate } from 'api/saq/types';
import { Layout } from 'layout/Layout';
import { Fragment, useState } from 'react';
import {
  EditableTableCell,
  EditBox,
  QuestionTableRow,
  TableCellWithBorder,
  TableHeadCell
} from 'screens/saq/SaqRisksTable.styles';
import { Check, Close } from '@material-ui/icons';
import { useUpdateQuestionScores } from 'api/saq/hooks/mutations';
import { changeQuestionScores } from 'api/saq/utils';
import { useQueryClient } from 'react-query';
import { SaqIdentifiers } from 'api/saq/resourcesUrl';

const companySizesArray = ['MICRO', 'SMALL', 'MEDIUM', 'LARGE'] as (keyof Answered)[];

type EditRowDetails = {
  questionCode?: string;
  scores: LabeledScore[],
  maxScore: number
};

const defaultRowDetails: EditRowDetails = {
  questionCode: undefined,
  scores: [],
  maxScore: -1
};

const numberInputProps = {
  type: 'number',
  inputMode: 'numeric',
  pattern: '[0-9]*',
  min: 0,
  max: 100
} as InputBaseComponentProps;

type EditableScoresTableProps<T extends QuestionnaireRisk> = {
  title: string,
  riskInfo: RiskInfo<T>,
  columns: ColumnInfo<T>[]
}

export const EditableScoresTable = <T extends QuestionnaireRisk>({
  title,
  riskInfo,
  columns
}: EditableScoresTableProps<T>) => {
  const queryClient = useQueryClient();
  const [editRowDetails, setEditRowDetails] = useState(defaultRowDetails);
  const { mutate: updateScores, isLoading: isUpdateLoading } = useUpdateQuestionScores();

  const isLastRow = (row: T, index: number) =>
    (row?.scores?.length || 0) - 1 === index;

  const showEditBox = (questionCode: string, scores: LabeledScore[], maxScore: number) => {
    setEditRowDetails({ questionCode, scores, maxScore });
  };

  const hideEditBox = () => {
    setEditRowDetails(defaultRowDetails);
  };

  const onSaveScores = async () => {
    const existingQuestionScore = riskInfo.allScores?.results
      ?.find((score) => editRowDetails.questionCode === score.questionCode);
    const changedQuestionScore = changeQuestionScores(
      existingQuestionScore?.scores || {} as Scores,
      editRowDetails.scores,
      editRowDetails.maxScore
    );
    await updateScores(
      { scoreId: existingQuestionScore?.id, scores: changedQuestionScore } as ScoreUpdate,
      {
        onSuccess: () => {
          const newAllScores = {
            ...riskInfo.allScores,
            results: riskInfo.allScores?.results.map((score) =>
              editRowDetails.questionCode === score.questionCode
                ? { ...score, scores: changedQuestionScore }
                : score
            )
          };
          queryClient.setQueryData([SaqIdentifiers.score], newAllScores);
          hideEditBox();
        },
        onError: (error) => {
          console.error(error.message);
          alert('Unable to update scores');
        }
      }
    );
  };

  const handleScoreChange = (value: string, companySize: string, rowLabel: string) => {
    const scoreRow = editRowDetails.scores.find((labeledScore) => labeledScore.label === rowLabel);
    // get rest size properties if we are deleting the value
    const { [companySize as keyof Answered]: _, ...rest } = scoreRow?.scores || {};
    const changedLabeledScore = {
      ...scoreRow,
      scores: value === ''
        ? rest
        : { ...scoreRow?.scores, [companySize]: Number.parseInt(value, 10) }
    } as LabeledScore;
    setEditRowDetails({
      ...editRowDetails,
      scores: [
        ...editRowDetails.scores.filter((labeledScore) => labeledScore.label !== rowLabel),
        changedLabeledScore
      ]
    });
  };

  const handleMaxScoreChange = (value: string) => {
    setEditRowDetails({ ...editRowDetails, maxScore: Number.parseInt(value, 10) });
  };

  const isMaxScoreInvalid =
    (editRowDetails.scores
      ?.flatMap((labeledScores) => labeledScores.scores)
      .flatMap((answerScore) => Object.values(answerScore || {})) as number[])
      .some((score) => editRowDetails.maxScore < score)
    || editRowDetails.maxScore > 100;

  return (
    <Layout
      title={title}
      isLoading={isUpdateLoading || riskInfo.isLoading}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow key={`${title} heading`}>
            {columns.map((column) => (
              <TableHeadCell key={column.name}>{column.name}</TableHeadCell>
            ))}
            <TableHeadCell>Possible answers</TableHeadCell>
            <TableHeadCell>Micro</TableHeadCell>
            <TableHeadCell>Small</TableHeadCell>
            <TableHeadCell>Medium</TableHeadCell>
            <TableHeadCell>Large</TableHeadCell>
            <TableHeadCell>Max</TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {riskInfo.data &&
            riskInfo.data.map((row: T) => (
              <Fragment key={`row-${row.questionCode}`}>
                {row && row?.scores?.map(
                  (scoreRow: LabeledScore, index: number) => (
                    <QuestionTableRow key={`score-${scoreRow.label}`} showDelimiter={isLastRow(row, index)}>
                      {index === 0 && columns.map((column) => (
                        <TableCellWithBorder
                          rowSpan={row.scores?.length || 0}
                          size="small"
                          isWide={'isSaq' in row}
                          key={column.property as string}
                        >
                          {column.property === 'isSaq'
                            ? row[column.property] ? 'SAQ' : 'Workers Info'
                            : row[column.property]
                          }
                        </TableCellWithBorder>
                      ))}
                      <TableCell scope="row" size="small" key="label">
                        {scoreRow.label}
                      </TableCell>
                      {companySizesArray.map((size) => (
                        editRowDetails.questionCode === row.questionCode
                          ? <TableCell scope="row" size="small" align="center" key={`score-${size}`}>
                            <EditBox
                              placeholder="-"
                              inputProps={numberInputProps}
                              defaultValue={scoreRow?.scores?.[size]}
                              onChange={(event) => handleScoreChange(event.target.value, size, scoreRow.label)}
                            />
                          </TableCell>
                          : <EditableTableCell
                            align="center"
                            size="small"
                            key={`score-${size}`}
                            onClick={() =>
                              showEditBox(row.questionCode, row.scores || [], row.maxScore || 0)
                            }
                          >
                            {scoreRow?.scores?.[size]}
                          </EditableTableCell>
                      ))}
                      {index === 0 && (
                        editRowDetails.questionCode === row.questionCode
                          ? <>
                            <TableCellWithBorder
                              scope="row"
                              size="small"
                              align="center"
                              key="maxScore"
                              rowSpan={row.scores?.length || 0}
                            >
                              <EditBox
                                placeholder="-"
                                defaultValue={row.maxScore}
                                inputProps={numberInputProps}
                                error={isMaxScoreInvalid}
                                onChange={(event) => handleMaxScoreChange(event.target.value)}
                              />
                            </TableCellWithBorder>
                            <TableCellWithBorder
                              scope="row"
                              size="small"
                              rowSpan={row.scores?.length || 0}
                              key="maxScoreActions"
                            >
                              <IconButton
                                size="small"
                                disabled={isMaxScoreInvalid}
                                onClick={onSaveScores}
                              >
                                <Check/>
                              </IconButton>
                              <IconButton size="small" onClick={() => hideEditBox()}><Close/></IconButton>
                            </TableCellWithBorder>
                          </>
                          : <TableCellWithBorder
                            align="center"
                            size="small"
                            key="maxScore"
                            rowSpan={row.scores?.length || 0}
                            onClick={() => showEditBox(
                              row.questionCode,
                              row.scores || [],
                              row.maxScore || 0
                            )}
                          >
                            {row.maxScore}
                          </TableCellWithBorder>
                      )}
                    </QuestionTableRow>
                  ))}
              </Fragment>
            ))}
        </TableBody>
      </Table>
    </Layout>
  );
};
